import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveDynamicComponent as _resolveDynamicComponent, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import getManagementOffice from '@/types/responses/getManagementOffice';
import getOperationOffice from '@/types/responses/getOperationOffice';
import { watch, computed, ref } from 'vue';
import { VAutocomplete, VCombobox } from 'vuetify/components';


export default /*@__PURE__*/_defineComponent({
  __name: 'BaseAutoCompleteWithSingleSelectedItem',
  props: {
    modelValue: {},
    label: {},
    itemName: {},
    items: {},
    initialItemId: {},
    managementOffice: {},
    operationOffices: {},
    errorMessages: {},
    readonly: { type: Boolean },
    disabled: { type: Boolean, default: false },
    selectFirstIfOnlyOne: { type: Boolean },
    clearable: { type: Boolean, default: true },
    isCombobox: { type: Boolean, default: false },
    mapToObject: {}
  },
  emits: ['update:modelValue', 'blur'],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit;

const searchText = ref('');

const modelValue = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    if (props.isCombobox && typeof value === 'string' && props.mapToObject) {
      emit('update:modelValue', props.mapToObject(value));
    } else {
      emit("update:modelValue", value);
    }
  },
})

// operationOffice
// managementOffice
// items
// が変更された時にdisplayItemsを更新する
watch(
  () => [props.operationOffices, props.managementOffice, props.items],
  () => {
    setInitialItem();
    checkIncludesItems()
  }
);

watch(() => props.initialItemId,() => {
  setInitialItem()
});

const normalize = (text: any): string => {
  const str = typeof text === 'string' ? text : typeof text?.title === 'string' ? text.title : ''
  return str
    .replace(/\s/g, '')
    .replace(/\u3000/g, '')
    .toLowerCase()
}

const checkMatch = (itemText: string, queryText: string) => {
  return normalize(itemText).includes(normalize(queryText))
}

// 権限によって絞り込んだitems。検索文字には依存しない。
const permittedItems = computed(() => {
  let result = props.items;
  if (props.managementOffice !== undefined && props.managementOffice !== null) {
    result = result.filter(
      (item) =>
        item.managementOfficeId === props.managementOffice?.id &&
        item.managementOfficeType === props.managementOffice?.type // management officeのIDは重複する可能性があるので、typeで絞り込む。
    );
  }

  if (
    props.operationOffices !== undefined &&
    props.operationOffices !== null &&
    props.operationOffices.length &&
    props.operationOffices[0]
  ) {
    result = result.filter((item) => {
      return props.operationOffices?.some((office) => {
        return office.id === item?.operationOfficeId
      })
    });
  }

  return result;
});

const filterItems = computed(() => {
  let result = permittedItems.value;

  // 検索文字でitemsを絞り込む
  let shouldFilter = !!searchText.value;

  if (shouldFilter && !props.isCombobox && props.modelValue) {
    const selectedItemLabel = props.modelValue[props.itemName];
    // v-autocompleteかつ、検索文字列が、現在選択されている項目の表示名と完全に一致している場合はフィルタリングをスキップ（全件表示）
    if (searchText.value === selectedItemLabel) {
      shouldFilter = false;
    }
  }

  if (shouldFilter) {
    result = result.filter(item => {
      const itemText = item[props.itemName];
      return checkMatch(itemText, searchText.value);
    });
  }

  // selectFirstIfOnlyOneがtrueの場合、displayItemsが1つの場合は選択する
  if (props.selectFirstIfOnlyOne && result.length === 1) {
    setSelectedItem(result.at(0));
  }

  return result
});

const setInitialItem = () => {
  if (props.initialItemId !== undefined) {
    // 初期値の選択は検索文字に左右されないよう、権限で絞り込んだitemsから探す。
    modelValue.value = permittedItems.value.find((item) => {
      return item.id === props.initialItemId
    });
  }
};

const setSelectedItem = (result: any) => {
  modelValue.value = result;
};

const checkIncludesItems = () => {
  if (!props.modelValue) {
    return;
  }
  if (!props.items || props.items.length === 0) {
    return;
  }
  if (props.isCombobox) {
    return;
  }
  // 空オブジェクトはどの項目にも対応しない値なので、未選択に戻す。
  if (Object.keys(props.modelValue).length === 0) {
    modelValue.value = null;
    return;
  }
  // modelValueの各項目がitemと一致するかで判定する。
  const isIncluded = permittedItems.value.some((item) =>
    Object.keys(props.modelValue).every(
      (key) => JSON.stringify(item[key]) === JSON.stringify(props.modelValue[key])
    )
  );
  if (!isIncluded) {
    modelValue.value = null;
  }
};

const emitBlur = () => {
  emit('blur');
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_resolveDynamicComponent(__props.isCombobox ? _unref(VCombobox) : _unref(VAutocomplete)), {
    label: __props.label,
    modelValue: modelValue.value,
    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((modelValue).value = $event)),
    search: searchText.value,
    "onUpdate:search": _cache[1] || (_cache[1] = ($event: any) => ((searchText).value = $event)),
    onBlur: emitBlur,
    "custom-filter": () => true,
    items: filterItems.value,
    "item-title": __props.itemName,
    "item-value": __props.itemName,
    "return-object": !__props.isCombobox,
    readonly: __props.readonly,
    disabled: __props.disabled,
    "error-messages": props.errorMessages,
    clearable: props.clearable
  }, null, 40, ["label", "modelValue", "search", "items", "item-title", "item-value", "return-object", "readonly", "disabled", "error-messages", "clearable"]))
}
}

})